import React, { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import Button from 'components/common/Button/Button';
import { RootStoreContext } from 'index';
import { ApplicationRoute } from 'routes/routes.enums';
import Spinner from 'components/common/Spinner/Spinner';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { notify } from 'components/common/Toast/toast.helpers';

import styles from './ExpiredSession.module.scss';
import { maskEmail } from '../RegistrationProcess/registrationProcess.utils';
import { ExpiredSessionPageType } from '../prequalification.enums';
import ResendEmailTimer from '../ResendEmailTimer/ResendEmailTimer';

const ExpiredSession = () => {
  const navigator = useNavigate();
  const { prequalificationStore } = useContext(RootStoreContext);
  const [isEmailResendTimerActive, setIsEmailResendTimerActive] = useState<boolean>(false);

  const {
    mainContact,
    assignedTeamMember,
    getExpiredSessionPageType,
    expiredSessionPageType,
    intermediaryApplicationId,
    isLoading,
    sendInvite,
  } = prequalificationStore;

  const handleLogIn = () => {
    navigator(ApplicationRoute.Login);
  };

  const handleSendEmail = async () => {
    setIsEmailResendTimerActive(true);

    try {
      await sendInvite(mainContact!.email);
      notify(ToastMessage.EmailSent, 'success');
    } catch (error) {
      notify(ToastMessage.DefaultError, 'error');
    }
  };

  useEffect(() => {
    getExpiredSessionPageType(intermediaryApplicationId!);
  }, []);

  return isLoading ? (
    <Spinner />
  ) : (
    <div className={styles.infoContainer}>
      <div className={styles.mainHeader}>Your session has expired. Please follow the steps below to continue.</div>
      <div className={styles.mainContainer}>
        <div className={styles.infoText}>
          {expiredSessionPageType === ExpiredSessionPageType.Login ? (
            <div>
              We can see that {mainContact?.firstName} {mainContact?.lastName}'s information has been previously
              submitted and we are currently waiting for additional information to be completed or cleared to finish
              onboarding process. Please log in to review the current status of your account.
            </div>
          ) : (
            <div>
              We can see that {mainContact?.firstName} {mainContact?.lastName}'s information has been previously
              submitted but the account sign up and and verification process was not completed. If you wish to complete
              this process, click the button below to have and email sent to{' '}
              <a className={styles.boldText}>{maskEmail(mainContact?.email!)}</a> to allow you to complete the sign-up
              process.
            </div>
          )}
        </div>
        {expiredSessionPageType === ExpiredSessionPageType.Login ? (
          <Button onClick={handleLogIn} className={styles.button}>
            Log In
          </Button>
        ) : (
          <div className={styles.sendEmailContainer}>
            <Button onClick={handleSendEmail} className={styles.button} disabled={isEmailResendTimerActive}>
              Send Email
            </Button>
            <div className={styles.timerContainer}>
              {isEmailResendTimerActive && <ResendEmailTimer onExpire={() => setIsEmailResendTimerActive(false)} />}
            </div>
          </div>
        )}
        {!assignedTeamMember?.isDefault && (
          <div className={styles.infoText}>
            if you need additional assistance, you can contact the HFS Account Manager below that was assigned to your
            account.
          </div>
        )}
        <div className={styles.teamMemberInfoContainer}>
          <div className={clsx(styles.teamMemberInfo, styles.boldText)}>{assignedTeamMember?.name}</div>
          <div className={styles.teamMemberInfo}>{assignedTeamMember?.position}</div>
          <div className={styles.teamMemberInfo}>HFS Financial</div>
        </div>
        <div className={clsx(styles.teamMemberInfo, styles.mainColorText)}>{assignedTeamMember?.phone}</div>
        <div className={clsx(styles.teamMemberInfo, styles.orangeText)}>{assignedTeamMember?.email}</div>
        <div className={clsx(styles.teamMemberInfo, styles.orangeText)}>www.hfsfin.com</div>
      </div>
    </div>
  );
};

export default observer(ExpiredSession);
